import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lightbulb, 
  Layers, 
  MousePointerClick, 
  Download,
  Sparkles,
  Check,
  Play,
  ChevronRight
} from 'lucide-react';

const AnimatedStepsDemo = ({ accentColor = 'primary', demoStyle = 'hollywood' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [subStep, setSubStep] = useState(0);
  const containerRef = useRef(null);

  // Color variants for different landing pages
  const colorMap = {
    primary: {
      accent: 'text-primary',
      accentBg: 'bg-primary',
      accentBorder: 'border-primary',
      gradient: 'from-primary/20 to-primary/5',
      glow: 'shadow-primary/20'
    },
    orange: {
      accent: 'text-orange-400',
      accentBg: 'bg-orange-500',
      accentBorder: 'border-orange-500',
      gradient: 'from-orange-500/20 to-pink-500/5',
      glow: 'shadow-orange-500/20'
    },
    green: {
      accent: 'text-green-400',
      accentBg: 'bg-green-500',
      accentBorder: 'border-green-500',
      gradient: 'from-green-500/20 to-emerald-500/5',
      glow: 'shadow-green-500/20'
    }
  };

  // Demo content for different styles
  const demoContent = {
    hollywood: {
      idea: "A burned-out astronaut on a solo Mars mission discovers an ancient alien signal that could change humanity's future — but responding means never returning home...",
      project: { title: "The Last Signal", genre: "Sci-Fi Drama" },
      choices: [
        "A: She decodes the message and finds coordinates to an alien archive",
        "B: The signal triggers buried memories of why she really left Earth",
        "C: Write your own direction..."
      ],
      scene: {
        heading: "INT. MARS HABITAT - NIGHT CYCLE",
        action: "Red dust coats the porthole windows. COMMANDER ELENA VASQUEZ (42), haunted eyes, stares at the blinking console. A message she wasn't supposed to find.",
        character: "ELENA",
        parenthetical: "(trembling)",
        dialogue: "Houston can never know about this."
      },
      bgImage: "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&q=60"
    },
    bollywood: {
      idea: "A rebellious daughter of a traditional Rajasthani family falls for a struggling street musician at her sister's wedding, but an old family secret threatens to tear them apart forever...",
      project: { title: "Dil Ki Awaaz", genre: "Romantic Drama" },
      choices: [
        "A: Priya discovers the musician is connected to her family's past",
        "B: Her father arranges a secret meeting that changes everything",
        "C: Write your own direction..."
      ],
      scene: {
        heading: "EXT. JAIPUR HAVELI ROOFTOP - SUNSET",
        action: "Golden light bathes the pink city. PRIYA (26), draped in silk, watches ARJUN play his sitar in the courtyard below. Their eyes meet. The music stops.",
        character: "PRIYA",
        parenthetical: "(whispering to herself)",
        dialogue: "Yeh kaisi mohabbat hai... jo mujhe tod bhi rahi hai, aur jod bhi."
      },
      bgImage: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=800&q=60"
    },
    nollywood: {
      idea: "A struggling chef in Lagos discovers her late grandmother's secret recipe book and enters a prestigious cooking competition to save her family restaurant from a corrupt developer...",
      project: { title: "Mama's Kitchen", genre: "Family Drama" },
      choices: [
        "A: A rival chef tries to sabotage her, revealing deeper family secrets",
        "B: She finds a hidden letter in the recipe book that changes everything",
        "C: Write your own direction..."
      ],
      scene: {
        heading: "INT. MAMA ADAEZE'S KITCHEN - DAWN",
        action: "Morning light streams through weathered windows. AMARA (28), determined eyes betraying sleepless nights, holds a worn recipe book to her chest.",
        character: "AMARA",
        parenthetical: "(clutching the book)",
        dialogue: "Grandma... they want to take everything. But I swear, your jollof will save us all."
      },
      bgImage: "https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?w=800&q=60"
    }
  };

  const content = demoContent[demoStyle] || demoContent.hollywood;
  const colors = colorMap[accentColor] || colorMap.primary;

  const steps = [
    {
      number: "1",
      title: "Share Your Idea",
      description: "Just describe your movie in a few sentences",
      icon: Lightbulb,
      demo: "idea"
    },
    {
      number: "2", 
      title: "AI Builds Structure",
      description: "Get a complete 3-act framework instantly",
      icon: Layers,
      demo: "structure"
    },
    {
      number: "3",
      title: "Choose Your Path",
      description: "Pick from options or write your own",
      icon: MousePointerClick,
      demo: "choices"
    },
    {
      number: "4",
      title: "Download Script",
      description: "Export in industry-standard format",
      icon: Download,
      demo: "export"
    }
  ];

  // Slower animation loop - 14 seconds total, 3.5s per step
  useEffect(() => {
    const stepDuration = 3500;
    
    const mainTimer = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % 4);
      setSubStep(0);
    }, stepDuration);

    // Sub-step animations within each step
    const subTimer = setInterval(() => {
      setSubStep((prev) => (prev + 1) % 4);
    }, stepDuration / 4);

    return () => {
      clearInterval(mainTimer);
      clearInterval(subTimer);
    };
  }, []);

  return (
    <div ref={containerRef} className="w-full">
      {/* Main Container */}
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        
        {/* Left Side - Steps List */}
        <div className="space-y-4">
          {steps.map((step, index) => {
            const isActive = currentStep === index;
            const isPast = currentStep > index;
            const StepIcon = step.icon;
            
            return (
              <motion.div
                key={step.number}
                initial={false}
                animate={{
                  scale: isActive ? 1.02 : 1,
                  opacity: isActive ? 1 : isPast ? 0.7 : 0.5
                }}
                transition={{ duration: 0.4 }}
                className={`relative flex items-start gap-4 p-4 rounded-lg border transition-all duration-500 ${
                  isActive 
                    ? `bg-gradient-to-r ${colors.gradient} ${colors.accentBorder} border-opacity-50 shadow-lg ${colors.glow}` 
                    : 'bg-white/5 border-white/10'
                }`}
              >
                {/* Step Number/Icon */}
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-500 ${
                  isActive ? `${colors.accentBg} text-black` : isPast ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'
                }`}>
                  {isPast ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <StepIcon className="w-5 h-5" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold uppercase tracking-wider ${isActive ? colors.accent : 'text-white/40'}`}>
                      Step {step.number}
                    </span>
                    {isActive && (
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        transition={{ duration: 3.5, ease: 'linear' }}
                        className={`h-0.5 ${colors.accentBg} rounded-full`}
                        style={{ maxWidth: '60px' }}
                      />
                    )}
                  </div>
                  <h3 className={`font-bold text-lg transition-colors duration-300 ${isActive ? 'text-white' : 'text-white/60'}`}>
                    {step.title}
                  </h3>
                  <p className={`text-sm transition-colors duration-300 ${isActive ? 'text-white/70' : 'text-white/40'}`}>
                    {step.description}
                  </p>
                </div>

                {/* Active Indicator */}
                {isActive && (
                  <motion.div
                    layoutId="activeIndicator"
                    className={`absolute right-4 top-1/2 -translate-y-1/2`}
                  >
                    <ChevronRight className={`w-5 h-5 ${colors.accent}`} />
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Right Side - Animated Demo Screen */}
        <div className="relative">
          {/* Background image */}
          {content.bgImage && (
            <div 
              className="absolute inset-0 rounded-xl overflow-hidden"
              style={{
                backgroundImage: `url(${content.bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.35,
                filter: 'blur(1px)'
              }}
            />
          )}
          
          <div className={`relative aspect-[4/3] bg-[#0a0a0a]/85 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden shadow-2xl ${colors.glow}`}>
            {/* Window Chrome */}
            <div className="h-8 bg-[#1a1a1a] border-b border-white/10 flex items-center px-3 gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/70" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
              <div className="w-3 h-3 rounded-full bg-green-500/70" />
              <span className="ml-3 text-xs text-white/40 font-mono">MovieBot — {content.project.title}</span>
            </div>

            {/* Demo Content */}
            <div className="h-[calc(100%-2rem)] p-4 overflow-hidden">
              <AnimatePresence mode="wait">
                {/* Step 1: Typing Idea */}
                {currentStep === 0 && (
                  <motion.div
                    key="idea"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className="h-full flex flex-col"
                  >
                    <div className="text-xs text-white/40 mb-2 font-mono">What's your movie about?</div>
                    <div className="flex-1 bg-black/50 rounded border border-white/10 p-3">
                      <TypewriterText 
                        text={content.idea}
                        colors={colors}
                        speed={20}
                      />
                    </div>
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: subStep >= 3 ? 1 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-3 flex justify-end"
                    >
                      <div className={`px-4 py-2 ${colors.accentBg} text-black text-xs font-bold rounded flex items-center gap-2`}>
                        <Sparkles className="w-3 h-3" />
                        Generate Structure
                      </div>
                    </motion.div>
                  </motion.div>
                )}

                {/* Step 2: Structure Appearing */}
                {currentStep === 1 && (
                  <motion.div
                    key="structure"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="h-full"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs text-white/40 font-mono">Your 3-Act Structure</div>
                      <div className={`text-xs ${colors.accent} font-medium`}>{content.project.genre}</div>
                    </div>
                    <div className="space-y-2">
                      {[
                        { act: 'ACT 1 — SETUP', beats: ['Opening Image', 'Theme Stated', 'Catalyst', 'Debate'] },
                        { act: 'ACT 2 — CONFRONTATION', beats: ['B Story', 'Fun & Games', 'Midpoint', 'All Is Lost'] },
                        { act: 'ACT 3 — RESOLUTION', beats: ['Dark Night', 'Break Into 3', 'Finale', 'Final Image'] }
                      ].map((act, actIdx) => (
                        <motion.div
                          key={act.act}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: actIdx * 0.4, duration: 0.5 }}
                          className="bg-black/30 rounded border border-white/10 p-2"
                        >
                          <div className={`text-xs font-bold ${colors.accent} mb-1`}>{act.act}</div>
                          <div className="flex flex-wrap gap-1">
                            {act.beats.map((beat, beatIdx) => (
                              <motion.span
                                key={beat}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: actIdx * 0.4 + beatIdx * 0.1 + 0.3, duration: 0.3 }}
                                className="text-[10px] px-2 py-0.5 bg-white/10 rounded text-white/60"
                              >
                                {beat}
                              </motion.span>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Choosing Options */}
                {currentStep === 2 && (
                  <motion.div
                    key="choices"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="h-full"
                  >
                    <div className="text-xs text-white/40 mb-2 font-mono">Choose your direction</div>
                    <div className="text-xs text-white/60 mb-3">What happens at the midpoint?</div>
                    <div className="space-y-2">
                      {content.choices.map((choice, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.2, duration: 0.4 }}
                          className={`p-3 rounded border text-xs transition-all duration-500 cursor-pointer ${
                            subStep >= 2 && idx === 0
                              ? `${colors.accentBorder} border-opacity-50 bg-gradient-to-r ${colors.gradient}`
                              : 'border-white/10 bg-black/30 hover:border-white/20'
                          }`}
                        >
                          <span className={`${subStep >= 2 && idx === 0 ? 'text-white' : 'text-white/60'}`}>
                            {choice}
                          </span>
                          {subStep >= 2 && idx === 0 && (
                            <motion.span
                              initial={{ opacity: 0, scale: 0 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.3 }}
                              className="float-right"
                            >
                              <Check className={`w-4 h-4 ${colors.accent}`} />
                            </motion.span>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Export Ready */}
                {currentStep === 3 && (
                  <motion.div
                    key="export"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="h-full flex flex-col"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs text-green-400 font-mono flex items-center gap-1">
                        <Check className="w-3 h-3" /> Script Complete
                      </div>
                      <div className="text-xs text-white/40">45 scenes • 112 pages</div>
                    </div>
                    
                    {/* Mini script preview */}
                    <div className="flex-1 bg-black/50 rounded border border-white/10 p-3 font-mono text-[10px] text-white/70 overflow-hidden">
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <div className={`${colors.accent} mb-1`}>FADE IN:</div>
                        <div className="text-white/90 mb-2">{content.scene.heading}</div>
                        <div className="text-white/60 mb-2">{content.scene.action}</div>
                        <div className="text-white/90">{content.scene.character}</div>
                        <div className="text-white/60 italic pl-4">{content.scene.parenthetical}</div>
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.8, duration: 0.5 }}
                          className="text-white/80 pl-4"
                        >
                          "{content.scene.dialogue}"
                        </motion.div>
                      </motion.div>
                    </div>

                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.4 }}
                      className="mt-3 flex gap-2"
                    >
                      <div className={`flex-1 px-4 py-2 ${colors.accentBg} text-black text-xs font-bold rounded flex items-center justify-center gap-2`}>
                        <Download className="w-3 h-3" />
                        Export PDF
                      </div>
                      <div className="px-4 py-2 bg-white/10 text-white text-xs font-bold rounded flex items-center justify-center gap-2">
                        <Play className="w-3 h-3" />
                        Continue
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Floating decorative elements */}
          <div className={`absolute -top-4 -right-4 w-20 h-20 border-2 ${colors.accentBorder} border-opacity-30 rounded-full pointer-events-none`} />
          <div className={`absolute -bottom-4 -left-4 w-16 h-16 ${colors.accentBg} rounded-full blur-xl opacity-30 pointer-events-none`} />
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2 mt-8">
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            animate={{
              scale: currentStep === i ? 1.3 : 1,
              opacity: currentStep === i ? 1 : 0.3
            }}
            transition={{ duration: 0.4 }}
            className={`w-2 h-2 rounded-full ${currentStep === i ? colors.accentBg : 'bg-white/30'}`}
          />
        ))}
      </div>
    </div>
  );
};

// Typewriter component with cursor
const TypewriterText = ({ text, colors, speed = 25 }) => {
  const [displayText, setDisplayText] = useState('');
  
  useEffect(() => {
    setDisplayText('');
    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
      }
    }, speed);
    
    return () => clearInterval(interval);
  }, [text, speed]);
  
  return (
    <span className="text-sm text-white/80">
      {displayText}
      <motion.span 
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.6, repeat: Infinity }}
        className={`inline-block w-0.5 h-4 ${colors.accentBg} ml-0.5 align-middle`}
      />
    </span>
  );
};

export default AnimatedStepsDemo;
